import { useMemo, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerHeaderWithLeftIcon } from "../../../components/Drawer/DrawerHeader";
import AutocompleteComponent from '../../../components/layout/Autocomplete';

import SearchIcon from '@mui/icons-material/Search';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Batch } from "../types/PeriodicosTypes";
import { useAssignPrestadorMutation, useGetPrestadoresQuery } from "../../../services/periodicosApi";
import useRegion from "../../../hooks/useRegion";
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';
import { Prestador } from '../consts/periodicosTypes';
import { BATCHES_TEST_IDS } from '../consts/testIds';

interface PeriodicosAssignPrestadorDrawerProps {
    selectedBatch: Batch | null;
    closeDrawer: () => void;
    isMassiveAssignation?: boolean;
    isReassignation?: boolean;
    selectedBatches?: Batch[];
    onSuccess?: () => void;
}

const PeriodicosAssignPrestadorDrawer = ({
    selectedBatch,
    closeDrawer,
    isMassiveAssignation = false,
    isReassignation = false,
    selectedBatches = [],
    onSuccess
}: PeriodicosAssignPrestadorDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const [providerName, setProviderName] = useState('');
    const [selectedProvider, setSelectedProvider] = useState<Prestador | null>(null);
    const {
        provincesList,
        citiesList,
        selectedProvince,
        selectedCity,
        handleProvinceChange,
        handleCityChange,
        isLoadingProvinces,
        isLoadingCities
    } = useRegion();

    const dispatch = useAppDispatch();
    const [assignPrestador, { isLoading: isAssigningPrestador }] = useAssignPrestadorMutation();

    const provinces =
        provincesList?.data?.provincias?.map((province) => ({
            id: province.id,
            nombre: province.nombre
        })) || [];

    const cities =
        citiesList?.data?.localidades?.map((city) => ({
            id: city.id,
            nombre: `${city.nombre} - ${city.codigoPostal}`
        })) || [];

    const { data: prestadores, isFetching: isFetchingPrestadores } = useGetPrestadoresQuery(
        {
            idProvincia: selectedProvince?.id,
            idLocalidad: selectedCity?.id
        },
        { skip: false }
    );

    const filteredPrestadores = useMemo(() => {
        const providers = Array.isArray(prestadores) ? prestadores : [];
        const normalizedName = providerName.trim().toLowerCase();

        if (!normalizedName) return providers;

        return providers.filter((prestador: any) =>
            (prestador?.razonSocial || '').toLowerCase().includes(normalizedName)
        );
    }, [prestadores, providerName]);

    const batchesToAssign = useMemo(() => {
        if (isMassiveAssignation) {
            return selectedBatches;
        }

        return selectedBatch ? [selectedBatch] : [];
    }, [isMassiveAssignation, selectedBatch, selectedBatches]);

    const handleAssignPrestadorClick = async () => {
        if (!selectedProvider?.id) {
            enqueueSnackbar(t('batches.drawers.assignPrestador.messages.selectProviderWarning'), {
                variant: 'warning'
            });
            return;
        }

        if (!batchesToAssign.length) {
            enqueueSnackbar(t('batches.drawers.assignPrestador.messages.noBatchesWarning'), {
                variant: 'warning'
            });
            return;
        }

        try {
            const adaptedPayload = {
                idPrestador: selectedProvider.id,
                idLotes: batchesToAssign.map((batch) => batch.id)
            };

            await assignPrestador(adaptedPayload).unwrap();
            enqueueSnackbar(t('batches.drawers.assignPrestador.messages.assignSuccess'), {
                variant: 'success'
            });
            onSuccess?.();
            closeDrawer();
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo asignar el prestador',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    return (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <DrawerHeaderWithLeftIcon
                onIconClick={closeDrawer}
                text={
                    isMassiveAssignation
                        ? t('batches.drawers.assignPrestador.titles.massive')
                        : isReassignation
                        ? t('batches.drawers.assignPrestador.titles.reassign')
                        : t('batches.drawers.assignPrestador.titles.assign')
                }
                subtitle={t('batches.drawers.assignPrestador.subtitle', {
                    count: batchesToAssign.length
                })}
            />

            {isReassignation && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        bgcolor: '#fffbeb',
                        border: '1px solid #fcd34d',
                        borderRadius: 2,
                        px: 2,
                        py: 1.5,
                        mt: 0,
                        flexShrink: 0
                    }}
                >
                    <WarningAmberOutlinedIcon fontSize="small" sx={{ color: '#d97706', flexShrink: 0, mt: 0.25 }} />
                    <Box>
                        <Typography sx={{ fontSize: '0.875rem', color: '#475569' }}>
                            {t('batches.dialogs.changePrestador.messagePrefix')}{' '}
                            <strong>{t('batches.states.inProgress')}</strong>.{' '}
                            {t('batches.dialogs.changePrestador.messageSuffix')}
                        </Typography>
                    </Box>
                </Box>
            )}

            <Box
                sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 1,
                    px: 2,
                    pt: 0,
                    pb: 0.5,
                    mt: isReassignation ? 2 : 0.5,
                    mb: 2,
                    flexShrink: 0,
                    backgroundColor: '#fff'
                }}
            >
                <Typography variant="subtitle2" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
                    {t('batches.drawers.assignPrestador.selectedBatches', {
                        count: batchesToAssign.length
                    })}
                </Typography>
                <Box
                    sx={{
                        maxHeight: 128,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5
                    }}
                >
                    {batchesToAssign.map((batch) => (
                        <Box
                            key={batch.id}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: 0.5 }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>
                                {`L${(batch.tipo || '').charAt(0).toUpperCase()}-${String(batch.id).padStart(3, '0')}`}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#64748b',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {batch.establecimiento?.nombre || '-'}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8', flexShrink: 0, marginLeft: 'auto' }}
                            >
                                {`${batch.direccion?.localidad || '-'}, ${batch.direccion?.provincia || '-'}`}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Typography
                sx={{
                    marginBottom: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderBottom: '1px solid #e2e8f0',
                    paddingBottom: 1,
                    flexShrink: 0
                }}
            >
                {t('batches.drawers.assignPrestador.search.title')}
            </Typography>

            <Grid container spacing={2} sx={{ flexShrink: 0 }}>
                <Grid item xs={6}>
                    <AutocompleteComponent
                        label={t('batches.drawers.assignPrestador.search.province')}
                        size="small"
                        name="idProvincia"
                        loading={isLoadingProvinces}
                        value={selectedProvince}
                        onChange={(value: any) => handleProvinceChange(value || null)}
                        options={provinces}
                        clearable
                        testID={BATCHES_TEST_IDS.drawers.assignPrestador.province}
                    />
                </Grid>
                <Grid item xs={6}>
                    <AutocompleteComponent
                        label={t('batches.drawers.assignPrestador.search.locality')}
                        size="small"
                        name="idLocalidad"
                        loading={isLoadingCities}
                        value={selectedCity}
                        onChange={(value: any) => handleCityChange(value || null)}
                        options={cities}
                        clearable
                        disabled={!selectedProvince}
                        testID={BATCHES_TEST_IDS.drawers.assignPrestador.locality}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: 'block', marginBottom: 0.5 }}
                    >
                        {t('batches.drawers.assignPrestador.search.providerName')}
                    </Typography>
                    <Box sx={{ position: 'relative' }}>
                        <input
                            placeholder={t('batches.drawers.assignPrestador.search.placeholder') as string}
                            type="text"
                            value={providerName}
                            onChange={(event) => setProviderName(event.target.value)}
                            data-testid={BATCHES_TEST_IDS.drawers.assignPrestador.providerName}
                            style={{
                                width: '100%',
                                height: 36,
                                paddingLeft: 36,
                                paddingRight: 12,
                                borderRadius: 4,
                                border: '1px solid #cbd5e1',
                                fontSize: '0.875rem',
                                fontFamily: 'inherit'
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 12,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#cbd5e1',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <SearchIcon fontSize="small" />
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, display: 'block', marginBottom: 1, flexShrink: 0 }}
                >
                    {t('batches.drawers.assignPrestador.search.results.title')}{' '}
                    <Typography component="span" variant="caption" sx={{ color: '#94a3b8' }}>
                        {isFetchingPrestadores
                            ? t('batches.drawers.assignPrestador.search.results.searching')
                            : t('batches.drawers.assignPrestador.search.results.found', {
                                  count: filteredPrestadores.length
                              })}
                    </Typography>
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        border: '1px solid #e2e8f0',
                        borderRadius: 1
                    }}
                >
                    {filteredPrestadores.map((prestador: any, idx: number) => (
                        <Box
                            key={prestador?.id || idx}
                            onClick={() => {
                                if (selectedProvider?.id === prestador?.id) {
                                    setSelectedProvider(null);
                                    setProviderName('');
                                } else {
                                    setSelectedProvider(prestador);
                                    setProviderName(prestador?.razonSocial || '');
                                }
                            }}
                            sx={{
                                p: 1.5,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#f8fafc' },
                                transition: 'colors 0.2s',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 1.5,
                                borderBottom: idx < filteredPrestadores.length - 1 ? '1px solid #f1f5f9' : 'none'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        p: 1,
                                        backgroundColor: '#f1f5f9',
                                        color: '#64748b',
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 32,
                                        height: 32
                                    }}
                                >
                                    <ApartmentOutlinedIcon fontSize="small" />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap color="#0f172a">
                                        {prestador?.razonSocial || '-'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#64748b',
                                            mt: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        <LocationOnOutlinedIcon fontSize="small" />{' '}
                                        {prestador?.localidad
                                            ? `${prestador.localidad.nombre}, ${prestador.localidad?.provincia?.nombre || '-'}`
                                            : '-'}
                                    </Typography>
                                </Box>
                            </Box>
                            <input
                                type="radio"
                                checked={selectedProvider?.id === prestador?.id}
                                onChange={(e) => e.stopPropagation()}
                                data-testid={`${BATCHES_TEST_IDS.drawers.assignPrestador.providerOptionPrefix}${prestador?.id}`}
                                style={{
                                    width: 16,
                                    height: 16,
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    marginTop: 4
                                }}
                                readOnly
                            />
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    pt: 2,
                    mt: 1,
                    borderTop: '1px solid #e2e8f0'
                }}
            >
                <Button
                    variant="outlined"
                    onClick={closeDrawer}
                    sx={{ mr: 1 }}
                    data-testid={BATCHES_TEST_IDS.drawers.assignPrestador.cancelButton}
                >
                    {t('batches.drawers.assignPrestador.buttons.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAssignPrestadorClick}
                    disabled={!selectedProvider || !batchesToAssign.length}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                    data-testid={BATCHES_TEST_IDS.drawers.assignPrestador.assignButton}
                >
                    {t('batches.drawers.assignPrestador.buttons.assign')}
                </Button>
            </Box>
            <BackdropComponent loading={isAssigningPrestador} />
        </Box>
    );
};

export { PeriodicosAssignPrestadorDrawer };
